import { tablemill } from "../tablemill_metal";
import { GraphForm } from "./graph_form";
import { Keyword } from "./keyword";
import { ConditionPanel } from "./condition_panel";
import { TargetDate } from "./target_date";
import { StatsGraph } from "./stats_graph";
import { DataDate } from "../model/data_date";
import { Series, SeriesSet } from "../model/series";
import $ from "jquery";

interface DateRange {
  start: DataDate;
  end: DataDate;
  start2?: DataDate;
}

interface QueryParams {
  type: string;
  partition: string;
  end: DataDate;
  dimension: string;
  area: string;
  category: string;
}

class SIGraphForm extends GraphForm {
  private static readonly SERIES_COLORS = ["#B8C12B", "#1F91CF"];

  private keyword: Keyword;
  private conditionPanel: ConditionPanel;
  private targetDate: TargetDate;

  constructor() {
    super();
    this.keyword = new Keyword($("#keyword"));
    this.conditionPanel = new ConditionPanel(this.formElement());
    this.targetDate = (this.conditionPanel as any).targetDate;
  }

  protected formElement(): JQuery {
    return $("#trends_form");
  }

  protected canvasElement(): JQuery {
    return $("#si_graph");
  }

  private typeElement(): JQuery {
    return $("#type");
  }

  private partitionElement(): JQuery {
    return $("#partition");
  }

  getAjaxUrl(): string {
    return this.canvasElement().data("ajaxUrl");
  }

  getPartition(): string {
    return this.partitionElement().val() as string;
  }

  getType(): string {
    return this.typeElement().val() as string;
  }

  initialize(): void {
    this.keyword.initialize();
    const invalidParameterCombinations = this.formElement().data(
      "invalidParameterCombinations",
    );
    this.conditionPanel.initialize(invalidParameterCombinations);
  }

  isYearlyQuery(): boolean {
    return this.getPartition() === "yearly";
  }

  keywordValue(): string {
    return this.keyword.value();
  }

  render(): void {
    const g = StatsGraph;
    const graph = new StatsGraph(this.canvasElement(), {
      colors: this.isYearlyQuery()
        ? [SIGraphForm.SERIES_COLORS[1]]
        : SIGraphForm.SERIES_COLORS,
      xAxis: g.AxisPolicy.forPartition(this.getPartition()),
      yAxis: g.FloatAxis,
      legend: g.WrappedSeriesLegend,
      todayLine: !this.isYearlyQuery(),
    });

    if ((this.keyword as any).isNotFound()) {
      graph.drawMessage(`
        「${this.keyword.value()}」は見付かりませんでした。
        別の単語をお試しください。
      `);
      return;
    }

    this.fetchSeriesSet(this.getAjaxUrl(), this.getQuery())
      .then((seriesSet: SeriesSet) => {
        graph.addSeriesSet(seriesSet);
        if (graph.hasNoData()) {
          graph.drawMessage(`
            この条件ではデータが存在しません。
            期間や集計単位を変更してお試しください。
          `);
        } else {
          graph.drawAll();
        }
      })
      .catch((res: any) => {
        graph.drawErrorMessage(`
          申し訳ありません、通信エラーが発生しました。
          少し時間をおいて再度お試しください。
          ステータス: ${res.status}, ${new Date()}
        `);
      })
      .finally(() => {
        this.enableCsvButton($(".si_graph_box .csv_button"));
      });
  }

  private getQuery(): QueryParams {
    const endDate = this.targetDate.getDataDate(this.getPartition());
    if (!endDate) {
      throw new Error("Target date is required");
    }

    return {
      type: this.getType(),
      partition: this.getPartition(),
      end: endDate,
      dimension: (this.conditionPanel as any).getDimension(),
      area: (this.conditionPanel as any).getArea(),
      category: (this.conditionPanel as any).getCategory(),
    };
  }

  // Draws two series:
  //   * Series #1: 1.5 year ago ... 0.5 year ago (startDate ... startDate2)
  //   * Series #2: 0.5 year ago ... "today" (startDate2 .. endDate)
  // We use the term "today" as "logical current day" in this function.
  // "today" is drawn as centerized red line.
  private fetchSeriesSet(url: string, params: QueryParams): Promise<SeriesSet> {
    if (this.isYearlyQuery()) {
      const dates = this.calculateYears(params.end);
      return new Promise((resolve, reject) => {
        $.ajax({
          url: url,
          data: this.overrideDates(params, dates),
          dataType: "json",
        })
          .done((response: any) => {
            const series = new Series(
              response.values,
              this.seriesName(dates.start, dates.end),
              dates.start,
            );
            resolve(new SeriesSet([series]));
          })
          .fail(reject);
      });
    } else {
      const dates = this.calculateDates(params.end);
      return new Promise((resolve, reject) => {
        $.ajax({
          url: url,
          data: this.overrideDates(params, dates),
          dataType: "json",
        })
          .done((response: any) => {
            resolve(this.splitSeries(response.values, dates));
          })
          .fail(reject);
      });
    }
  }

  private calculateYears(targetDate: DataDate): DateRange {
    const end = targetDate.partitionStartDate();
    // See also app/models/query_builder.rb cramp_param_range_to_ten_year function
    const crampedYear = Math.max(
      (tablemill as any).FIRST_DATA_DATE.getFullYear(),
      end.year() - 10,
    );
    const crampedDate = new Date(
      crampedYear,
      (tablemill as any).FIRST_DATA_DATE.getMonth(),
      (tablemill as any).FIRST_DATA_DATE.getDate(),
    );
    const start = new DataDate(crampedDate, "yearly");
    return { start, end };
  }

  private calculateDates(targetDate: DataDate): DateRange {
    const end = targetDate.partitionStartDate();
    const start2 = end.halfYearAgo();
    const start = start2.yearsAgo(1);
    return { start, start2, end };
  }

  private overrideDates(
    params: QueryParams,
    dates: DateRange,
  ): Record<string, any> {
    return Object.assign({}, params, {
      start: dates.start.ymd(),
      end: dates.end.ymd(),
    });
  }

  private splitSeries(values: any[], dates: DateRange): SeriesSet {
    const threshold = dates.start2!.toDate();

    const prev: any[] = [];
    const curr: any[] = [];

    values.forEach(([d, s]) => {
      if (DataDate.getDateForYMD(d) < threshold) {
        prev.push([d, s]);
      } else {
        curr.push([d, s]);
      }
    });

    const series1 = new Series(
      prev,
      this.seriesName(dates.start, dates.start2!),
      dates.start,
    );
    const series2 = new Series(
      curr,
      this.seriesName(dates.start2!, dates.end),
      dates.start2!,
    );

    return new SeriesSet([series1, series2]);
  }

  private seriesName(begin: DataDate, end: DataDate): string {
    return `${begin.year()}/${begin.month()} - ${end.year()}/${end.month()}`;
  }
}

export { SIGraphForm };
